import { useEffect, useState } from 'react';

import { useDebouncedValue } from '@tanstack/react-pacer/debouncer';
import { Search } from 'lucide-react';

import { Input } from '@nesso/ui/primitives/input';

import type { SessionNoteStatus } from '@/entities/session';

import {
    PastSessionsStatusFilter,
    type PastSessionsStatusFilterValue,
} from './past-sessions-search/past-sessions-status-filter';

export type Props = {
    onSearchDebouncedChange: (value: string) => void;
    onStatusChange: (value: SessionNoteStatus | undefined) => void;
    debounceMs?: number;
    placeholder?: string;
    initialValue?: string;
    initialStatus?: SessionNoteStatus;
};

export function PastSessionsSearch({
    onSearchDebouncedChange,
    onStatusChange,
    debounceMs = 500,
    placeholder = 'Search by name',
    initialValue = '',
    initialStatus,
}: Props) {
    const [value, setValue] = useState(initialValue);
    const [debouncedValue] = useDebouncedValue(value, { wait: debounceMs });
    const [status, setStatus] = useState<PastSessionsStatusFilterValue>(initialStatus ?? 'ALL');

    useEffect(() => {
        onSearchDebouncedChange(debouncedValue);
    }, [debouncedValue, onSearchDebouncedChange]);

    useEffect(() => {
        onStatusChange(status === 'ALL' ? undefined : status);
    }, [onStatusChange, status]);

    return (
        <div className="flex gap-3">
            <div className="relative flex-1 min-w-0 max-w-[300px]">
                <Search
                    className="absolute left-3 top-1/2 -translate-y-[calc(50%-0.00625rem)] h-4 w-4 text-muted-foreground pointer-events-none"
                    aria-hidden="true"
                />
                <Input
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="pl-10 h-9"
                    aria-label="Search for a session"
                    autoFocus={false}
                />
            </div>
            <PastSessionsStatusFilter value={status} onValueChange={setStatus} />
        </div>
    );
}
